import { useState } from "react";
import { shopApi, SectionFormValues, SectionMargins } from "api/shop";
import { useToaster } from "utils";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { PaziaModal } from "components/molecules";
import { IDropdownOption, PaziaInput } from "components/atoms";
import { SECTION_TYPES, SECTION_TYPE_OPTIONS } from "consts/sectionTypes";
import { SectionMarginsConfig, marginsSchema, MARGINS_FORM_NAME } from "./SectionForms/SectionMarginsConfig";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { normalizeSectionMargins } from "./normalizeSectionMargins";
import { CourseGroupListForm, CourseGroupListFormValues } from "./SectionForms/CourseGroupListForm";
import { SimpleWysiwygForm, SimpleWysiwygFormValues } from "./SectionForms/SimpleWysiwygForm";
import { TwoColumnsWysiwygForm, TwoColumnsWysiwygFormValues } from "./SectionForms/TwoColumnsWysiwygForm";
import { ImageContentForm, ImageContentFormValues } from "./SectionForms/ImageContentForm";
import { ImagesCardsForm, ImagesCardsFormValues } from "./SectionForms/ImagesCardsForm";
import { LogosSectionForm, LogosSectionFormValues } from "./SectionForms/LogosSectionForm";
import { SimpleBgImageForm, SimpleBgImageFormValues } from "./SectionForms/SimpleBgImageForm";
import { ClassicHeroForm, ClassicHeroFormValues } from "./SectionForms/ClassicHeroForm";
import { DynamicSectionForm, DynamicSectionFormValues } from "./SectionForms/DynamicSectionForm";
import { MainPageHeroForm, MainPageHeroFormValues } from "./SectionForms/MainPageHeroForm";
import { ProductsSectionForm, ProductsSectionFormValues } from "./SectionForms/ProductsSectionForm";
import { MapSectionForm, MapSectionFormValues } from "./SectionForms/MapSectionForm";
import { OpinionsSectionForm, OpinionsSectionFormValues } from "./SectionForms/OpinionsSectionForm";
import { ContactLocationsForm, ContactLocationsFormValues } from "./SectionForms/ContactLocationsForm";
import { ContactFormSectionForm, ContactFormSectionFormValues } from "./SectionForms/ContactFormSectionForm";
import { PeopleSectionForm, PeopleSectionFormValues } from "./SectionForms/PeopleSectionForm";
import { CardsSectionForm, CardsSectionFormValues } from "./SectionForms/CardsSectionForm";
import { MobileStickyCtaForm, MobileStickyCtaFormValues } from "./SectionForms/MobileStickyCtaForm";
import { StaticPdfForm, StaticPdfFormValues } from "./SectionForms/StaticPdfForm";
import { VideoSectionForm, VideoSectionFormValues } from "./SectionForms/VideoSectionForm";
import { VideoContentCardsForm, VideoContentCardsFormValues } from "./SectionForms/VideoContentCardsForm";
import { BlogQuoteForm, BlogQuoteFormValues } from "./SectionForms/BlogQuoteForm";
import { PaziaDropdown } from "components/atoms/PaziaDropdown/PaziaDropdown";
import { FormProvider, useForm } from "react-hook-form";

interface AddSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  landingPageId: number;
  onSuccess: () => void;
  typeOptions?: IDropdownOption[];
  onCreate?: (data: SectionFormValues) => Promise<void>;
}

export const AddSectionModal = ({
  isOpen,
  onClose,
  landingPageId,
  onSuccess,
  typeOptions,
  onCreate,
}: AddSectionModalProps) => {
  const { showSuccess, showError } = useToaster();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [sectionName, setSectionName] = useState("");
  const [anchorIdValue, setAnchorIdValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const commonSchema = yup.object({
    type: yup.string(),
    name: yup.string(),
    anchorId: yup.string(),
    [MARGINS_FORM_NAME]: marginsSchema.optional(),
  });

  const methods = useForm({
    resolver: yupResolver(commonSchema) as any,
    defaultValues: {
      type: "",
      name: "",
      anchorId: "",
      [MARGINS_FORM_NAME]: undefined as SectionMargins | undefined,
    },
  });

  const handleTypeSelect = (type: string, name: string, anchorId: string) => {
    setSelectedType(type);
    setSectionName(name);
    setAnchorIdValue(anchorId);
  };

  const handleSubmit = async (content: CourseGroupListFormValues | SimpleWysiwygFormValues | TwoColumnsWysiwygFormValues | ImageContentFormValues | ImagesCardsFormValues | LogosSectionFormValues | SimpleBgImageFormValues | ClassicHeroFormValues | DynamicSectionFormValues | MainPageHeroFormValues | ProductsSectionFormValues | MapSectionFormValues | OpinionsSectionFormValues | ContactLocationsFormValues | ContactFormSectionFormValues | PeopleSectionFormValues | CardsSectionFormValues | MobileStickyCtaFormValues | StaticPdfFormValues | VideoSectionFormValues | VideoContentCardsFormValues | BlogQuoteFormValues) => {
    if (!selectedType) return;

    setIsLoading(true);
    try {
      const rawMargins = methods.getValues(MARGINS_FORM_NAME) as Partial<SectionMargins> | undefined;
      const margins = normalizeSectionMargins(rawMargins);
      const data: SectionFormValues = {
        type: selectedType,
        name: sectionName || undefined,
        anchorId: anchorIdValue || undefined,
        content,
        ...(margins ? { margins } : {}),
      };
      if (onCreate) {
        await onCreate(data);
      } else {
        await shopApi.createSection(landingPageId, data);
      }
      showSuccess("Dodano nową sekcję");
      handleClose();
      onSuccess();
    } catch (e) {
      showError("Nie udało się dodać sekcji");
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setSectionName("");
    setAnchorIdValue("");
    methods.reset();
    onClose();
  };

  return (
    <PaziaModal id="add-section-modal" onCloseRequest={handleClose} isOpen={isOpen} size="full">
      {isOpen && (
        <ModalPadding>
          <ModalHeader>Dodaj sekcję</ModalHeader>

          {!selectedType ? (
            <div className="space-y-4">
              <p className="text-slate-600 dark:text-slate-400">Wybierz typ sekcji:</p>
              <FormProvider {...methods}>
                <div className="grid max-w-md gap-4">
                  <PaziaInput name="name" label="Nazwa sekcji" placeholder="np. Sekcja główna" />
                  <div>
                    <PaziaInput name="anchorId" label="Identyfikator (anchor)" placeholder="np. kursy" />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Używany w linkach: #kursy</p>
                  </div>
                  <PaziaDropdown
                    name="type"
                    label="Typ sekcji"
                    options={typeOptions ?? (SECTION_TYPE_OPTIONS as IDropdownOption[])}
                    required
                  />
                </div>
              </FormProvider>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeSelect(methods.getValues("type"), methods.getValues("name"), methods.getValues("anchorId"))}
                  disabled={!methods.watch("type")}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Dalej
                </button>
              </div>
            </div>
          ) : (
            <FormProvider {...methods}>
              <div className="mb-4 max-w-3xl">
                <SectionMarginsConfig />
              </div>
              {selectedType === SECTION_TYPES.COURSE_GROUP_LIST && (
                <CourseGroupListForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.SIMPLE_WYSIWYG && (
                <SimpleWysiwygForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.TWO_COLUMNS_WYSIWYG && (
                <TwoColumnsWysiwygForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.IMAGE_CONTENT && (
                <ImageContentForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.IMAGES_CARDS && (
                <ImagesCardsForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.LOGOS_SECTION && (
                <LogosSectionForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.SIMPLE_BG_IMAGE && (
                <SimpleBgImageForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.CLASSIC_HERO && (
                <ClassicHeroForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.DYNAMIC_SECTION && (
                <DynamicSectionForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.MAIN_PAGE_HERO && (
                <MainPageHeroForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.PRODUCTS_SECTION && (
                <ProductsSectionForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.MAP_SECTION && (
                <MapSectionForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.OPINIONS_SECTION && (
                <OpinionsSectionForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.CONTACT_LOCATIONS && (
                <ContactLocationsForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.CONTACT_FORM && (
                <ContactFormSectionForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.PEOPLE_SECTION && (
                <PeopleSectionForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.CARDS_SECTION && (
                <CardsSectionForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.MOBILE_STICKY_CTA && (
                <MobileStickyCtaForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.STATIC_PDF && (
                <StaticPdfForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.VIDEO_SECTION && (
                <VideoSectionForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.VIDEO_CONTENT_CARDS && (
                <VideoContentCardsForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
              {selectedType === SECTION_TYPES.BLOG_QUOTE && (
                <BlogQuoteForm
                  onSubmit={handleSubmit}
                  onCancel={() => setSelectedType(null)}
                  isLoading={isLoading}
                />
              )}
            </FormProvider>
          )}
        </ModalPadding>
      )}
    </PaziaModal>
  );
};
